/**
 * PropertyAmenitiesSummary — Tier 0 booking-local read-only amenity chip block
 * for the create-reservation sales-info rail (SCRUM-141 / DS-010-5, step 9).
 *
 * The create-reservation form already shows the selected property's identity +
 * occupancy via `<SalesInfoPanel>`. This is a SEPARATE, adjacent read-only block
 * that lists the property's assigned amenities so a phone-in agent can quick-
 * confirm the unit ("does it have Wi-Fi / a pool?"). It composes the property
 * slice's `usePropertyAmenities` READ hook via the PUBLIC barrel
 * (`../../property/index.js`) — the only sanctioned cross-module import path
 * (CLAUDE.md barrel-only rule). It does NOT deep-import the picker components
 * or `SelectableChip` (those stay module-private in the property slice).
 *
 * **Scope.** Read-only. Flattens every group's amenities into a flat list and
 * renders ONE kit `<Badge>` per amenity showing its `label`. The raw
 * `amenityId` UUID and the per-amenity `note` are NEVER shown — the sales-info
 * surface is a quick confirm, not the editor.
 *
 * **Failure posture.** This is a NON-CRITICAL rail add-on. Every non-ready
 * status (`loading` / `forbidden` / `not-found` / `error`) and a null
 * `propertyId` render NOTHING (`null`) — the block must never surface an error
 * or a loading skeleton into the sales rail. Only a `ready` result draws: with
 * amenities it lists the chips; with zero amenities it shows a compact
 * empty-state so the operator knows none are recorded (not that the block
 * failed to load).
 *
 * **Tokens (ADR frontend-0003, SCRUM-78/79).** Semantic `var(--token)`s only —
 * no hex, no inline pixel literals; mirrors `_components/SalesInfoPanel.tsx` and
 * `property/_components/OTAChip.tsx`. **A11y (WCAG 2.1 AA).** Semantic HTML with
 * a heading that names the region; the badge list is carried by TEXT (label per
 * chip), never color alone; axe-clean.
 */

import * as React from 'react';
import { Badge } from '@sparta/ui-kit';
import { usePropertyAmenities } from '../../property/index.js';

export interface PropertyAmenitiesSummaryProps {
  /** The selected property's id, or `null` when no property is chosen yet. */
  readonly propertyId: string | null;
}

export function PropertyAmenitiesSummary({
  propertyId,
}: PropertyAmenitiesSummaryProps): React.ReactElement | null {
  const headingId = React.useId();
  const amenities = usePropertyAmenities(propertyId);

  // Non-critical rail add-on: loading / forbidden / not-found / error (and a
  // null propertyId, which the hook reports as `loading`) all render nothing.
  if (amenities.status !== 'ready') {
    return null;
  }

  // `ready` — flatten every group's amenities into a single read-only list.
  const flat = amenities.data.flatMap((group) => group.amenities);

  return (
    <section
      aria-labelledby={headingId}
      data-testid="property-amenities-summary"
      className="rounded-2xl border border-solid border-[var(--border-subtle)] bg-[var(--bg-surface)] p-5"
    >
      <h2
        id={headingId}
        className="m-0 mb-3 text-sm font-semibold text-[color:var(--fg-secondary)]"
      >
        Amenities
      </h2>

      {flat.length === 0 ? (
        <p className="m-0 text-sm text-[color:var(--fg-tertiary)]">
          No amenities listed.
        </p>
      ) : (
        <ul className="m-0 flex flex-wrap gap-2 p-0">
          {flat.map((amenity) => (
            <li key={amenity.amenityId} className="list-none">
              <Badge variant="neutral">{amenity.label}</Badge>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
